import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import {
  PROJECT_STATIONS,
  CURRENT_LOCATIONS_LEGACY,
  STATUSES_FOR_LOCATION,
  STORAGE_DETAILS,
  PREP_QUEUE_STATUSES,
  WORKFLOW_STAGES,
  PRIORITIES,
  formatLocalDateTime,
  formatLocalDate,
} from "../lib/constants";
import AuthImg from "../components/AuthImg";
import CrewLeaderPicker from "../components/CrewLeaderPicker";
import SubstationCombobox from "../components/SubstationCombobox";
import WorkflowProgressBar from "../components/WorkflowProgressBar";
import CommentsThread from "../components/CommentsThread";
import DeleteShipmentDialog from "../components/DeleteShipmentDialog";
import { DeductStockDialog } from "../components/DeductStockDialog";
import { PriorityBadge, EtaBadge } from "../components/Badges";
import { useAuth } from "../contexts/AuthContext";
import {
  Save,
  ChevronLeft,
  Upload,
  X,
  ChevronDown,
  ChevronUp,
  History,
  PlayCircle,
  PackageCheck,
  ListPlus,
  HardHat,
  Undo2,
  Handshake,
  Truck,
  MapPin,
  Megaphone,
  CalendarClock,
  AlertTriangle,
  Camera,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function ShipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, isPrivileged, isRunnerLevel, isViewer, role } = useAuth();
  const canDelete = isPrivileged;
  const [shipment, setShipment] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // History entry deletion (admin/supervisor only)
  const [historyDeleteTarget, setHistoryDeleteTarget] = useState(null); // {id, field_changed, ...}
  const [historyDeleteReason, setHistoryDeleteReason] = useState("");
  const [historyDeleting, setHistoryDeleting] = useState(false);

  // Modals
  const [destockOpen, setDestockOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverLocation, setDeliverLocation] = useState("");
  const [deliverImages, setDeliverImages] = useState([]);
  const [deliverImgUploading, setDeliverImgUploading] = useState(false);

  // Auto stock-deduction modal — fired before "Preparing" / "Completed"
  // transitions on the QuickBtn shortcuts. Holds the pending status to apply
  // after the user resolves the modal.
  const [pendingPrepChange, setPendingPrepChange] = useState(null); // { status, label }

  const [editable, setEditable] = useState({
    Project_Station: "",
    Assigned_Crew_Leader: "",
    Requestor_Message: "",
    Current_Location: "",
    Other_Location: "",
    Storage_Detail: "",
    Storage_Other: "",
    Status: "",
    Workflow_Stage: "",
    Preparation_Queue_Status: "",
    Priority: "Normal",
    Minor_Materials: "",
    Equipment_Needed: "",
    Unavailable_Items: "",
    Desired_Date: "",
    Notes: "",
    Images: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shipments/${id}`);
      setShipment(data);
      setEditable({
        Project_Station: data.Project_Station || "",
        Assigned_Crew_Leader: data.Assigned_Crew_Leader || "",
        Requestor_Message: data.Requestor_Message || "",
        Current_Location: data.Current_Location,
        Other_Location: data.Other_Location || "",
        Storage_Detail: data.Storage_Detail || "",
        Storage_Other: data.Storage_Other || "",
        Status: data.Status,
        Workflow_Stage: data.Workflow_Stage || "Received",
        Preparation_Queue_Status: data.Preparation_Queue_Status,
        Priority: data.Priority || "Normal",
        Minor_Materials: data.Minor_Materials || "",
        Equipment_Needed: data.Equipment_Needed || "",
        Unavailable_Items: data.Unavailable_Items || "",
        Desired_Date: data.Desired_Date || "",
        Notes: data.Notes || "",
        Images: data.Images || [],
      });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Not found");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/shipments/${id}/history`);
      setHistory(data);
    } catch (e) {
      // History is a non-critical panel — log so dev tools / monitors see the
      // failure but don't surface a toast (would be noisy on every detail open).
      console.error("Failed to load shipment history", e);
    }
  };

  useEffect(() => {
    load();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = (k, v) =>
    setEditable((s) => {
      const next = { ...s, [k]: v };
      if (k === "Current_Location") {
        if (v !== "Main yard") {
          next.Storage_Detail = "";
          next.Storage_Other = "";
          if (
            ![
              "Received",
              "In yard",
              "At station",
              "Installed",
              "Returned",
            ].includes(next.Status)
          ) {
            next.Status = "Received";
          }
        }
      }
      if (k === "Storage_Detail" && v !== "Other Storage") {
        next.Storage_Other = "";
      }
      return next;
    });

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageUploading(true);
    try {
      const newPaths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      setEditable((s) => ({ ...s, Images: [...s.Images, ...newPaths] }));
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const onDeliverUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setDeliverImgUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDeliverImages((curr) => [...curr, data.path]);
      }
      toast.success(`Added ${files.length} delivery photo${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setDeliverImgUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (path) => {
    setEditable((s) => ({ ...s, Images: s.Images.filter((p) => p !== path) }));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    if (
      editable.Current_Location === "Main yard" &&
      !(editable.Storage_Detail || "").trim()
    ) {
      const msg = "Stocking Location is required when Current Location is Main yard.";
      setError(msg);
      toast.error(msg);
      setSaving(false);
      return;
    }
    try {
      const { data } = await api.patch(`/shipments/${id}`, editable);
      setShipment(data);
      loadHistory();
      toast.success("Shipment updated");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Save failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const callWorkflow = async (path, body, msg) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/shipments/${id}/${path}`, body || {});
      setShipment(data);
      setEditable((s) => ({
        ...s,
        Current_Location: data.Current_Location,
        Storage_Detail: data.Storage_Detail || "",
        Storage_Other: data.Storage_Other || "",
        Status: data.Status,
        Workflow_Stage: data.Workflow_Stage,
        Other_Location: data.Other_Location || "",
        Images: data.Images || s.Images,
      }));
      loadHistory();
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const doDestock = () => {
    setDestockOpen(false);
    callWorkflow("destock", null, "Destocked & loaded to truck");
  };

  const doDeliver = () => {
    const body = { location: deliverLocation, images: deliverImages };
    setDeliverOpen(false);
    setDeliverLocation("");
    setDeliverImages([]);
    callWorkflow("deliver", body, "Marked Delivered at Station");
  };

  const doInstall = () => callWorkflow("install", null, "Marked Installed");

  const doDeleteHistory = async () => {
    if (!historyDeleteTarget) return;
    const reason = historyDeleteReason.trim();
    if (reason.length < 3) {
      toast.error("A reason of at least 3 characters is required");
      return;
    }
    setHistoryDeleting(true);
    try {
      await api.delete(
        `/shipments/${id}/history/${historyDeleteTarget.id}`,
        { data: { reason } },
      );
      toast.success("History entry removed");
      setHistoryDeleteTarget(null);
      setHistoryDeleteReason("");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setHistoryDeleting(false);
    }
  };

  const doDelete = async (reason, notes, confirm) => {
    setDeleting(true);
    try {
      await api.delete(`/shipments/${id}`, { data: { reason, notes, confirm } });
      toast.success(`Deleted MR ${shipment?.MR_Number || ""}`);
      setDeleteOpen(false);
      nav("/shipments", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto p-6 text-slate-500">Loading shipment…</div>;
  }
  if (!shipment) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rg-card p-6 text-rose-700 font-bold">{error || "Not found"}</div>
        <Link to="/shipments" className="text-sm font-bold text-slate-700 underline mt-3 block">
          Back to list
        </Link>
      </div>
    );
  }

  const stage = shipment.Workflow_Stage || "Received";
  const isCrewRequest = shipment.Status === "Requested by Crew" || stage === "Requested by Crew";

  // RBAC: who can edit / use workflow buttons on THIS shipment
  const cl = (shipment.Assigned_Crew_Leader || "").trim().toLowerCase();
  const myName = (user?.name || "").trim().toLowerCase();
  const myEmail = (user?.email || "").trim().toLowerCase();
  const isMyCrewLeaderRecord = !!cl && (
    (myName && (cl === myName || cl.includes(myName))) ||
    (myEmail && (cl === myEmail || cl.includes(myEmail)))
  );
  const canEdit = isRunnerLevel || (role === "crew_leader" && isMyCrewLeaderRecord);
  const canRunWorkflow = isRunnerLevel; // stock/destock/deliver/install — runner level only

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-44 md:pb-12">
      <div className="flex items-center justify-between mb-3 gap-2">
        <button
          onClick={() => nav(-1)}
          data-testid="back-btn"
          className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        {canDelete && (
          <button
            onClick={() => setDeleteOpen(true)}
            data-testid="detail-delete-btn"
            className="rg-tap px-3 border-2 border-rose-600 hover:border-rose-900 hover:bg-rose-50 rounded-sm flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-rose-700"
            title="Delete this shipment (admin / supervisor only)"
          >
            <Trash2 strokeWidth={2.5} className="h-3.5 w-3.5" />
            Delete
          </button>
        )}
      </div>

      {/* Header card */}
      <div
        className={`rg-card p-5 mb-4 border-l-[6px] ${
          isCrewRequest ? "border-l-purple-700" : "border-l-slate-900"
        }`}
      >
        {isCrewRequest && (
          <div
            className="inline-flex items-center gap-1.5 bg-purple-700 text-white px-3 py-1 rounded-sm font-display uppercase tracking-wider text-xs mb-2"
            data-testid="detail-crew-request-banner"
          >
            <Megaphone strokeWidth={3} className="h-4 w-4" />
            Crew Request
          </div>
        )}
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">Shipment</div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1
            className="font-display text-3xl sm:text-4xl text-slate-900 font-mono"
            data-testid="detail-mr"
          >
            {shipment.MR_Number}
          </h1>
          {shipment.Priority && shipment.Priority !== "Normal" && (
            <PriorityBadge value={shipment.Priority} testid="detail-priority-badge" size="lg" />
          )}
          {shipment.Estimated_Ready_Time && (
            <EtaBadge
              etaText={shipment.Estimated_Ready_Time}
              etaIso={shipment.Estimated_Ready_Iso}
              position={shipment.Estimated_Ready_Position}
              testid="detail-eta-badge"
              size="lg"
            />
          )}
        </div>

        {shipment.Desired_Date && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 border-2 border-amber-400 text-amber-900 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wide"
            data-testid="detail-needed-by"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            Needed by {formatLocalDate(shipment.Desired_Date)}
          </div>
        )}

        {shipment.Current_Location === "Main yard" && !shipment.Storage_Detail && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 border-2 border-amber-400 text-amber-900 px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wide"
            data-testid="detail-stocking-needed-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Stocking Location Needed
          </div>
        )}

        {shipment.Unavailable_Items && (
          <div
            className="mt-3 flex items-start gap-2 bg-rose-50 border-2 border-rose-600 px-3 py-2 rounded-sm"
            data-testid="detail-unavailable-banner"
          >
            <AlertTriangle strokeWidth={2.5} className="h-5 w-5 text-rose-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">
                Unavailable / Missing items
              </div>
              <div className="text-rose-900 font-bold">{shipment.Unavailable_Items}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
          <ReadOnlyItem label="Project / Station" value={shipment.Project_Station} />
          <ReadOnlyItem label="Contents" value={shipment.Contents || "—"} />
          <ReadOnlyItem
            label="Received"
            value={formatLocalDateTime(shipment.Received_DateTime)}
          />
          <ReadOnlyItem
            label="Last Location Change"
            value={formatLocalDateTime(shipment.Last_Location_Change)}
          />
        </div>
      </div>

      {/* 4-stage Workflow Progress Bar */}
      <div className="rg-card p-4 sm:p-5 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
          Yard Runner Workflow
        </div>
        <WorkflowProgressBar stage={stage} testid="detail-workflow-progress" />
      </div>

      {/* Sequential workflow quick actions — runner level only */}
      {canRunWorkflow ? (
        <WorkflowActions
          stage={stage}
          onStock={() => nav(`/shipments/${id}/stock`)}
          onDestock={() => setDestockOpen(true)}
          onDeliver={() => setDeliverOpen(true)}
          onInstall={doInstall}
          saving={saving}
        />
      ) : (
        <div
          className="rg-card p-3 mb-4 bg-slate-100 border-2 border-dashed border-slate-300 text-center text-xs font-bold uppercase tracking-wider text-slate-500"
          data-testid="wf-actions-readonly"
        >
          Workflow controls are restricted to Yard Runner / Supervisor / Admin
        </div>
      )}
      {/* Editable fields */}
      {canEdit ? (
      <div className="rg-card p-5 mb-4">
        <h2 className="font-display text-lg uppercase text-slate-900 mb-4">Update fields</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Project / Station" className="sm:col-span-2">
            <SubstationCombobox
              value={editable.Project_Station}
              onChange={(v) => setField("Project_Station", v)}
              testid="detail-project-station"
              extraOptions={PROJECT_STATIONS}
            />
          </Field>
          <Field label="Workflow Stage">
            <Select
              testid="detail-workflow-stage"
              value={editable.Workflow_Stage}
              onChange={(v) => setField("Workflow_Stage", v)}
              options={WORKFLOW_STAGES}
            />
          </Field>
          <Field label="Priority">
            <Select
              testid="detail-priority"
              value={editable.Priority}
              onChange={(v) => setField("Priority", v)}
              options={PRIORITIES}
            />
          </Field>
          <Field label="Current Location">
            <Select
              testid="detail-current-location"
              value={editable.Current_Location}
              onChange={(v) => setField("Current_Location", v)}
              options={CURRENT_LOCATIONS_LEGACY}
            />
          </Field>
          <Field label="Status">
            <Select
              testid="detail-status"
              value={editable.Status}
              onChange={(v) => setField("Status", v)}
              options={STATUSES_FOR_LOCATION(editable.Current_Location)}
            />
          </Field>
          {editable.Current_Location === "Other" && (
            <Field label="Other location" className="sm:col-span-2">
              <input
                value={editable.Other_Location}
                onChange={(e) => setField("Other_Location", e.target.value)}
                data-testid="detail-other-location"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </Field>
          )}
          {editable.Current_Location === "Main yard" && (
            <div
              className="sm:col-span-2 border-2 border-amber-400 bg-amber-50 rounded-sm p-4 space-y-4"
              data-testid="detail-storage-block"
            >
              <div className="flex items-start gap-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
                  Stocking Location
                </div>
                <div className="text-xs text-amber-800">Where it will be stored in Main yard</div>
              </div>
              <Field label="Stocking Location *">
                <Select
                  testid="detail-storage-detail"
                  value={editable.Storage_Detail}
                  onChange={(v) => setField("Storage_Detail", v)}
                  options={["", ...STORAGE_DETAILS]}
                  placeholderText="— Pick a stocking spot —"
                />
              </Field>
              {editable.Storage_Detail === "Other Storage" && (
                <Field label="Other storage (describe)">
                  <input
                    value={editable.Storage_Other}
                    onChange={(e) => setField("Storage_Other", e.target.value)}
                    data-testid="detail-storage-other"
                    className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                  />
                </Field>
              )}
            </div>
          )}
          <Field label="Preparation Queue" className="sm:col-span-2">
            <Select
              testid="detail-prep-status"
              value={editable.Preparation_Queue_Status}
              onChange={(v) => setField("Preparation_Queue_Status", v)}
              options={PREP_QUEUE_STATUSES}
            />
          </Field>
          <Field label="Assigned Crew Leader (give to)" className="sm:col-span-2">
            <CrewLeaderPicker
              value={editable.Assigned_Crew_Leader}
              onChange={(v) => setField("Assigned_Crew_Leader", v)}
              testid="detail-crew-leader-picker"
            />
          </Field>
          <Field label="Requestor message (what & when)" className="sm:col-span-2">
            <textarea
              value={editable.Requestor_Message}
              onChange={(e) => setField("Requestor_Message", e.target.value)}
              data-testid="detail-requestor-message"
              rows={3}
              className="w-full border-2 border-purple-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
              placeholder="Original crew request explanation"
            />
          </Field>
          <Field label="Desired date">
            <input
              type="date"
              value={editable.Desired_Date}
              onChange={(e) => setField("Desired_Date", e.target.value)}
              data-testid="detail-desired-date"
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Minor materials (lugs, bolts, etc.)" className="sm:col-span-2">
            <textarea
              value={editable.Minor_Materials}
              onChange={(e) => setField("Minor_Materials", e.target.value)}
              data-testid="detail-minor-materials"
              rows={3}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Equipment needed (Meggar, IR tester, etc.)" className="sm:col-span-2">
            <textarea
              value={editable.Equipment_Needed}
              onChange={(e) => setField("Equipment_Needed", e.target.value)}
              data-testid="detail-equipment-needed"
              rows={2}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Unavailable / missing items (red — visible to all)" className="sm:col-span-2">
            <textarea
              value={editable.Unavailable_Items}
              onChange={(e) => setField("Unavailable_Items", e.target.value)}
              data-testid="detail-unavailable-items"
              rows={2}
              className="w-full border-2 border-rose-400 rounded-sm px-3 py-2 text-base bg-rose-50 focus:border-rose-700 outline-none font-bold text-rose-900"
              placeholder="Note anything missing or back-ordered"
            />
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <textarea
              value={editable.Notes}
              onChange={(e) => setField("Notes", e.target.value)}
              data-testid="detail-notes"
              rows={3}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
            />
          </Field>
          <Field label="Photos" className="sm:col-span-2">
            <div className="space-y-3">
              <label
                htmlFor="detail-image-upload"
                className="rg-tap w-full bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer"
                data-testid="detail-image-upload-label"
              >
                <Upload strokeWidth={2.5} className="h-5 w-5" />
                {imageUploading ? "Uploading…" : "Add photos"}
              </label>
              <input
                id="detail-image-upload"
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={onUpload}
                className="hidden"
                data-testid="detail-image-upload-input"
              />
              {editable.Images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {editable.Images.map((p) => (
                    <div key={p} className="relative aspect-square">
                      <AuthImg
                        path={p}
                        className="w-full h-full object-cover border-2 border-slate-300 rounded-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(p)}
                        className="absolute -top-2 -right-2 h-7 w-7 bg-rose-700 text-white grid place-items-center rounded-sm border-2 border-white"
                      >
                        <X strokeWidth={3} className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
        </div>

        <div className="mt-5 pt-4 border-t-2 border-slate-200 text-xs text-slate-500">
          Last updated{" "}
          <span className="text-slate-700 font-bold">
            {formatLocalDateTime(shipment.Last_Updated)}
          </span>{" "}
          by{" "}
          <span className="text-slate-700 font-bold" data-testid="detail-last-updated-by">
            {shipment.Last_Updated_By || "—"}
          </span>
        </div>
      </div>
      ) : (
        <div
          className="rg-card p-4 mb-4 bg-slate-50 border-2 border-dashed border-slate-300"
          data-testid="detail-readonly-banner"
        >
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
            Read-only access
          </div>
          <div className="text-sm text-slate-700">
            {isViewer
              ? "Regular crew members have read-only access. Contact your supervisor for changes."
              : "You can only edit shipments where you are the Assigned Crew Leader."}
          </div>
        </div>
      )}

      {/* Comments thread */}
      <CommentsThread shipmentId={id} />

      {/* History accordion */}
      <div className="rg-card mb-4">
        <button
          onClick={() => setHistoryOpen((s) => !s)}
          data-testid="toggle-history-btn"
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
        >
          <span className="font-display text-base uppercase text-slate-900 flex items-center gap-2">
            <History className="h-5 w-5" />
            History ({history.length})
          </span>
          {historyOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
        {historyOpen && (
          <div className="border-t-2 border-slate-200 p-4" data-testid="history-list">
            {history.length === 0 ? (
              <div className="text-sm text-slate-500">No changes yet.</div>
            ) : (
              <ul className="space-y-3">
                {history.map((h) => (
                  <li key={h.id} className="text-sm border-l-2 border-slate-300 pl-3">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>{formatLocalDateTime(h.timestamp)}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{h.changed_by_name}</span>
                        {isPrivileged && h.field_changed !== "history_entry_deleted" && (
                          <button
                            onClick={() => {
                              setHistoryDeleteTarget(h);
                              setHistoryDeleteReason("");
                            }}
                            data-testid={`history-delete-btn-${h.id}`}
                            className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider border border-rose-300 hover:border-rose-700 px-1.5 py-0.5 rounded-sm"
                            title="Delete this history entry (audit-logged)"
                          >
                            <Trash2 strokeWidth={2.5} className="h-3 w-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-slate-800">
                      <span className="font-bold uppercase tracking-wide text-xs text-amber-700">
                        {h.field_changed}
                      </span>
                      :{" "}
                      <span className="line-through text-slate-400">
                        {h.old_value || "(empty)"}
                      </span>{" "}
                      → <span className="font-bold">{h.new_value || "(empty)"}</span>
                      {h.notes && (
                        <span className="block text-[11px] text-slate-500 mt-0.5">{h.notes}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm mb-4">
          {error}
        </div>
      )}

      {/* Save bar (sticky on mobile) — secondary actions */}
      {canEdit && (
      <div className="fixed md:static bottom-16 md:bottom-auto left-0 right-0 z-30 bg-white md:bg-transparent border-t-2 md:border-t-0 border-slate-300 p-3 md:p-0 space-y-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <QuickBtn
            testid="qa-mark-returned"
            onClick={() => callWorkflow("install", null, "")}
            color="rose"
            icon={Undo2}
            disabled
          >
            (legacy)
          </QuickBtn>
          <QuickBtn
            testid="qa-add-prep-queue"
            onClick={() =>
              api
                .patch(`/shipments/${id}`, { Preparation_Queue_Status: "Pending Preparation" })
                .then(() => {
                  toast.success("Added to Prep Queue");
                  load();
                })
            }
            color="amber"
            icon={ListPlus}
          >
            Add to Queue
          </QuickBtn>
          <QuickBtn
            testid="qa-mark-preparing"
            onClick={() => setPendingPrepChange({ status: "Preparing", label: "Marked Preparing" })}
            color="blue"
            icon={PlayCircle}
          >
            Preparing
          </QuickBtn>
          <QuickBtn
            testid="qa-ready-handover"
            onClick={() =>
              api
                .patch(`/shipments/${id}`, { Preparation_Queue_Status: "Ready for Handover" })
                .then(() => {
                  toast.success("Ready for Handover");
                  load();
                })
            }
            color="purple"
            icon={Handshake}
          >
            Handover
          </QuickBtn>
        </div>
        <button
          onClick={save}
          disabled={saving || imageUploading}
          data-testid="save-changes-btn"
          className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Save strokeWidth={3} className="h-5 w-5" />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
      )}

      {/* Danger zone — admin / supervisor only */}
      {canDelete && (
        <div
          className="rg-card mt-8 p-5 border-2 border-rose-300 bg-rose-50/40"
          data-testid="detail-danger-zone"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 grid place-items-center bg-rose-700 text-white rounded-sm flex-shrink-0">
              <Trash2 strokeWidth={2.5} className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-700">
                Danger zone — Admin / Supervisor only
              </div>
              <div className="font-display text-lg uppercase text-slate-900 mt-0.5 leading-tight">
                Delete this shipment
              </div>
              <p className="text-sm text-slate-700 mt-1">
                Permanently removes <span className="font-mono font-bold">{shipment?.MR_Number}</span> and all its
                history, comments, and notifications. Audit-logged with the reason you select.
              </p>
            </div>
          </div>
          <button
            onClick={() => setDeleteOpen(true)}
            data-testid="detail-delete-btn-bottom"
            className="rg-tap-xl w-full bg-rose-700 hover:bg-rose-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-rose-900"
          >
            <Trash2 strokeWidth={3} className="h-5 w-5" />
            Delete This Shipment
          </button>
        </div>
      )}

      {/* Delete confirmation modal — admin / supervisor only */}
      <DeleteShipmentDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        mrNumber={shipment?.MR_Number}
        busy={deleting}
        onConfirm={doDelete}
        testIdPrefix="delete"
      />

      {/* Auto stock-deduction modal — fires before Preparing/Completed transitions */}
      {pendingPrepChange && (
        <DeductStockDialog
          shipmentId={id}
          open={true}
          onResolved={() => {
            const { status, label } = pendingPrepChange;
            setPendingPrepChange(null);
            api
              .patch(`/shipments/${id}`, { Preparation_Queue_Status: status })
              .then(() => {
                toast.success(label);
                load();
              })
              .catch(() => toast.error("Update failed"));
          }}
          onCancel={() => setPendingPrepChange(null)}
        />
      )}

      {/* Destock confirmation modal */}
      <AlertDialog open={destockOpen} onOpenChange={setDestockOpen}>
        <AlertDialogContent data-testid="destock-confirm-modal">          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-700" />
              Destock & Load to Truck?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Material will be removed from yard storage{" "}
              {shipment.Storage_Detail ? (
                <span className="font-bold">({shipment.Storage_Detail})</span>
              ) : null}{" "}
              and marked as on a truck. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="destock-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDestock}
              data-testid="destock-confirm-btn"
              className="bg-orange-700 hover:bg-orange-800 text-white"
            >
              Yes, Destock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deliver modal — location + recommended photo */}
      <AlertDialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <AlertDialogContent data-testid="deliver-modal" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-700" />
              Mark Delivered at Station
            </AlertDialogTitle>
            <AlertDialogDescription>
              Capture the exact drop location and (recommended) a photo for proof of delivery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Drop location *
              </label>
              <input
                value={deliverLocation}
                onChange={(e) => setDeliverLocation(e.target.value)}
                data-testid="deliver-location-input"
                placeholder="e.g. TSS 45 control house"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </div>
            <div className="border-2 border-dashed border-amber-400 bg-amber-50 rounded-sm p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Add photo (recommended)
              </div>
              <label
                htmlFor="deliver-photo-upload"
                className="rg-tap w-full bg-white border-2 border-amber-500 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer min-h-[56px]"
                data-testid="deliver-photo-label"
              >
                <Camera strokeWidth={2.5} className="h-5 w-5" />
                {deliverImgUploading ? "Uploading…" : `Add proof photo${deliverImages.length ? ` (${deliverImages.length})` : ""}`}
              </label>
              <input
                id="deliver-photo-upload"
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={onDeliverUpload}
                className="hidden"
                data-testid="deliver-photo-input"
              />
              {deliverImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {deliverImages.map((p) => (
                    <AuthImg
                      key={p}
                      path={p}
                      className="aspect-square w-full object-cover border-2 border-slate-300 rounded-sm"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deliver-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeliver}
              disabled={!deliverLocation.trim() || deliverImgUploading}
              data-testid="deliver-confirm-btn"
              className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
            >
              Mark Delivered
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History entry delete confirmation modal — admin / supervisor only */}
      <AlertDialog
        open={!!historyDeleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryDeleteTarget(null);
            setHistoryDeleteReason("");
          }
        }}
      >
        <AlertDialogContent data-testid="history-delete-modal" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-700" />
              Delete history entry?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the history row from the timeline and writes an immutable audit
              record. A reason is required for compliance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {historyDeleteTarget && (
            <div className="rg-card p-3 bg-slate-50 text-xs space-y-1">
              <div>
                <span className="font-bold uppercase tracking-wide text-amber-700">
                  {historyDeleteTarget.field_changed}
                </span>
                : <span className="line-through text-slate-400">{historyDeleteTarget.old_value || "(empty)"}</span>{" "}
                → <span className="font-bold">{historyDeleteTarget.new_value || "(empty)"}</span>
              </div>
              <div className="text-slate-500">
                {formatLocalDateTime(historyDeleteTarget.timestamp)} by{" "}
                <span className="font-bold">{historyDeleteTarget.changed_by_name}</span>
              </div>
            </div>
          )}
          <div className="py-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Reason for deletion *
            </label>
            <textarea
              value={historyDeleteReason}
              onChange={(e) => setHistoryDeleteReason(e.target.value)}
              data-testid="history-delete-reason-input"
              rows={3}
              placeholder="e.g. Incorrect entry, duplicate of #12345, PII to redact"
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-rose-700 outline-none"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Min 3 characters. This reason is permanently logged in the audit trail.
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="history-delete-cancel-btn" disabled={historyDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteHistory}
              disabled={historyDeleting || historyDeleteReason.trim().length < 3}
              data-testid="history-delete-confirm-btn"
              className="bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50"
            >
              {historyDeleting ? "Deleting…" : "Delete entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WorkflowActions({ stage, onStock, onDestock, onDeliver, onInstall, saving }) {
  // Compute primary next-step button + secondary buttons based on stage
  const buttons = [];

  if (stage === "Received" || stage === "Requested by Crew") {
    buttons.push({
      key: "stock",
      label: "Confirm Stocked in Yard",
      sub: "Step 2 — pick bay/rack/shelf",
      icon: PackageCheck,
      onClick: onStock,
      color: "amber",
      testid: "wf-go-to-stock",
    });
  }
  if (stage === "Stocked") {
    buttons.push({
      key: "destock",
      label: "Destock & Load to Truck",
      sub: "Step 3 — clear yard storage",
      icon: Truck,
      onClick: onDestock,
      color: "orange",
      testid: "wf-destock-btn",
    });
  }
  if (stage === "Destocked") {
    buttons.push({
      key: "deliver",
      label: "Mark Delivered at Station",
      sub: "Step 4 — drop at site (photo recommended)",
      icon: MapPin,
      onClick: onDeliver,
      color: "emerald",
      testid: "wf-deliver-btn",
    });
  }
  if (stage === "Delivered") {
    buttons.push({
      key: "install",
      label: "Mark as Installed",
      sub: "Final — material is in place",
      icon: HardHat,
      onClick: onInstall,
      color: "green",
      testid: "wf-install-btn",
    });
  }

  if (buttons.length === 0) {
    // Installed / Returned — show informational chip instead
    return (
      <div className="rg-card p-4 mb-4 bg-slate-100 border-2 border-slate-300 text-center" data-testid="wf-actions-done">
        <div className="font-display text-sm uppercase tracking-wider text-slate-700">
          Workflow complete — {stage}
        </div>
      </div>
    );
  }

  const COLORS = {
    amber: "bg-amber-500 hover:bg-amber-600 text-slate-900 border-amber-700",
    orange: "bg-orange-700 hover:bg-orange-800 text-white border-orange-900",
    emerald: "bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-900",
    green: "bg-green-700 hover:bg-green-800 text-white border-green-900",
  };

  return (
    <div className="space-y-2 mb-4" data-testid="wf-actions">
      {buttons.map(({ key, label, sub, icon: Icon, onClick, color, testid }) => (
        <button
          key={key}
          onClick={onClick}
          disabled={saving}
          data-testid={testid}
          className={`rg-tap-xl w-full border-2 rounded-sm font-display uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-60 min-h-[72px] text-base sm:text-lg ${COLORS[color]}`}
        >
          <Icon strokeWidth={3} className="h-6 w-6" />
          <div className="flex flex-col items-start leading-tight">
            <span>{label}</span>
            <span className="text-[10px] font-bold opacity-80 normal-case tracking-normal">{sub}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testid, placeholderText }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
    >
      {options.map((o) =>
        o === "" ? (
          <option key="__empty" value="">
            {placeholderText || "— Select —"}
          </option>
        ) : (
          <option key={o} value={o}>
            {o}
          </option>
        )
      )}
    </select>
  );
}

function ReadOnlyItem({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-0.5">
        {label}
      </div>
      <div className="text-sm text-slate-900 font-bold break-words">{value}</div>
    </div>
  );
}

function QuickBtn({ children, onClick, color, icon: Icon, testid, disabled }) {
  const map = {
    blue: "bg-blue-600 hover:bg-blue-700 border-blue-800",
    rose: "bg-rose-700 hover:bg-rose-800 border-rose-900",
    amber: "bg-amber-500 hover:bg-amber-600 border-amber-700 text-slate-900",
    purple: "bg-purple-700 hover:bg-purple-800 border-purple-900",
  };
  const isAmber = color === "amber";
  if (disabled) return null;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rg-tap rounded-sm font-display uppercase tracking-wide text-sm flex items-center justify-center gap-1.5 border-2 ${map[color]} ${
        isAmber ? "" : "text-white"
      }`}
    >
      <Icon strokeWidth={3} className="h-4 w-4" />
      {children}
    </button>
  );
}
